import {
  areGenericConfigsLoaded,
  getActPlaceholderImagePath,
  getExcursionLevelLabelFromGroundType,
  getExcursionsLevelLabels,
  modalOnDocumentReady,
} from '@msc-b2c/shared-utils';
import { initNewCarousel } from './carousel';

const parseHTML = (element: HTMLElement, str: string) => {
  const parser = new DOMParser();
  const formattedDescription = parser.parseFromString(str, 'text/html');
  element.innerHTML = formattedDescription.body.innerHTML;
};

const hideRibbonIfEmpty = (wrapper: HTMLElement | null) => {
  if (wrapper && !wrapper.querySelector('.excursion__modal__ribbon[data-display="true"]')) {
    wrapper.classList.add('hide');
  }
};

const hideEmptyDetailItems = (wrapper: HTMLElement | null) => {
  if (!wrapper) return;
  const items = wrapper.querySelectorAll<HTMLElement>('.excursion__modal__details-item');
  items.forEach((item) => {
    if (!item.querySelector('.excursion__modal-details-item[data-display="true"]')) {
      item.classList.add('hide');
    }
  });
};

const hasAuthoredExcursionLevelLabels = (): boolean => {
  const labels = getExcursionsLevelLabels();
  return Boolean(
    labels?.excursionLevelEasy || labels?.excursionLevelMedium || labels?.excursionLevelDifficult
  );
};

let excursionLevelLabelsReady: Promise<void> | null = null;

function waitForExcursionLevelLabels(timeout = 5000, interval = 100): Promise<void> {
  if (!excursionLevelLabelsReady) {
    excursionLevelLabelsReady = new Promise((resolve) => {
      const start = Date.now();

      const check = () => {
        if (
          hasAuthoredExcursionLevelLabels() ||
          areGenericConfigsLoaded() ||
          Date.now() - start > timeout
        ) {
          resolve();
        } else {
          setTimeout(check, interval);
        }
      };

      check();
    });
  }

  return excursionLevelLabelsReady;
}

const applyDifficultyLabel = (el: HTMLElement, level: string | null | undefined) => {
  const text = getExcursionLevelLabelFromGroundType(level);
  if (!text) {
    el.classList.add('hide');
    el.setAttribute('hidden', 'true');
    return;
  }

  el.querySelector('.detail-difficulty__button-wrapper')?.remove();

  const label = el.querySelector('p');
  if (!label) return;

  let bold = label.querySelector('b');
  if (!bold) {
    label.appendChild(document.createTextNode(' '));
    bold = document.createElement('b');
    label.appendChild(bold);
  }
  bold.textContent = text;
};

const handleDifficulty = (el: HTMLElement | null, level: string | null | undefined) => {
  if (!el) return;

  if (hasAuthoredExcursionLevelLabels() || areGenericConfigsLoaded()) {
    applyDifficultyLabel(el, level);
    return;
  }

  waitForExcursionLevelLabels()
    .then(() => applyDifficultyLabel(el, level))
    .catch(console.error);
};

const handleLanguages = (el: HTMLElement | null, langs: string | null | undefined) => {
  if (!el || !langs) return;
  const boldElement = el.querySelector('b');
  if (boldElement) {
    boldElement.textContent = langs.split(',').join(' ');
  }
};

const injectDescriptions = (el: HTMLElement | null, htmlStr: string | null | undefined) => {
  if (!el || !htmlStr) return;
  parseHTML(el, htmlStr);
};

const injectShortDescription = (el: HTMLElement | null, htmlStr: string | null | undefined) => {
  if (!el || !htmlStr) return;
  const parsed = new DOMParser().parseFromString(htmlStr, 'text/html');
  el.innerHTML = parsed.body.innerHTML;
};

const modalHandler = (modal: HTMLElement) => {
  hideRibbonIfEmpty(document.querySelector('.excursion__modal__ribbon-wrapper'));
  hideEmptyDetailItems(modal.querySelector('.excursion__modal__details-wrapper'));

  handleDifficulty(
    modal.querySelector('.detail-difficulty'),
    modal.querySelector('.detail-difficulty')?.getAttribute('data-difficulty')
  );

  handleLanguages(
    modal.querySelector('.detail-languages'),
    modal.querySelector('.detail-languages')?.getAttribute('data-languages')
  );

  injectDescriptions(
    modal.querySelector('.excursion__modal__description'),
    modal.querySelector('.excursion__modal__description')?.getAttribute('data-desc')
  );

  injectShortDescription(
    modal.querySelector('.excursion__modal__description-short'),
    modal.querySelector('.excursion__modal__description-short')?.getAttribute('data-desc')
  );
};

// Function to create and append the elements
const createSwiperActions = (
  id: string,
  carouseNextIconLabel: string,
  carouselPrevIconLabel: string
) => {
  // Create the main container
  const swiperActions = document.createElement('div');
  swiperActions.className = 'swiper-actions';

  // Create the navigators container
  const navigators = document.createElement('div');
  navigators.className = 'navigators';

  // Create the previous button
  const prevButton = document.createElement('button');
  prevButton.className = `swiper-button-prev--carousel-${id} swiper-button-prev swiper-button`;
  prevButton.type = 'button';
  prevButton.setAttribute('aria-label', carouselPrevIconLabel);
  prevButton.setAttribute('data-cmp-hook-carousel', 'previous');
  prevButton.setAttribute('tabindex', '0');

  // Create the left icon for previous button
  const leftIcon = document.createElement('i');
  leftIcon.className = 'icon icon-left';
  leftIcon.setAttribute('aria-hidden', 'false');
  leftIcon.setAttribute('title', carouselPrevIconLabel);
  leftIcon.tabIndex = -1;

  // Append left icon to previous button
  prevButton.appendChild(leftIcon);

  // Create the progress wrapper
  const progressWrapper = document.createElement('div');
  progressWrapper.className = 'swiper-progress-wrapper';

  // Create the scrollbar
  const scrollbar = document.createElement('div');
  scrollbar.className = `swiper-scrollbar--carousel-${id} swiper-scrollbar`;

  // Append scrollbar to progress wrapper
  progressWrapper.appendChild(scrollbar);

  // Create the next button
  const nextButton = document.createElement('button');
  nextButton.className = `swiper-button-next--carousel-${id} swiper-button-next swiper-button`;
  nextButton.type = 'button';
  nextButton.setAttribute('aria-label', carouseNextIconLabel);
  nextButton.setAttribute('data-cmp-hook-carousel', 'previous');
  nextButton.setAttribute('tabindex', '0');

  // Create the right icon for next button
  const rightIcon = document.createElement('i');
  rightIcon.className = 'icon icon-right';
  rightIcon.setAttribute('aria-hidden', 'false');
  rightIcon.setAttribute('title', carouseNextIconLabel);
  rightIcon.tabIndex = -1;

  // Append right icon to next button
  nextButton.appendChild(rightIcon);

  // Append elements to navigators
  navigators.appendChild(prevButton);
  navigators.appendChild(progressWrapper);
  navigators.appendChild(nextButton);

  swiperActions.appendChild(navigators);

  return swiperActions;
};

const extractModals = (container: Element) => {
  const targetDiv = document.querySelector('.root.container');
  const modals = Array.from(container.querySelectorAll('.cmp-container__modal'));
  if (targetDiv) {
    modals.forEach((div) => {
      modalHandler(div as HTMLElement);
      targetDiv.appendChild(div); // Moves the div and its content
    });
  }
};

const initiateCarouselOnAis = (container: Element) => {
  const initEl = container.querySelector('.ais-Hits');
  const wrapper = container.querySelector('.ais-Hits-list');
  const items = container.querySelectorAll('.ais-Hits-item');
  const containerID = container.id ?? 'default';
  const actionColor = container.classList.contains('-dark') ? '-dark' : '-light';
  const carouselNextIconLabel = container.getAttribute('data-carousel-next-icon') ?? '';
  const carouselPrevIconLabel = container.getAttribute('data-carousel-prev-icon') ?? '';

  if (wrapper) {
    wrapper.classList.add('swiper-wrapper');
  }
  if (items) {
    items.forEach((el: Element, index: number) => {
      el.id = `carousel-${containerID}-item-${index}`;
      el.classList.add('swiper-slide');
    });
  }

  if (initEl) {
    initEl.id = `carousel-${containerID}`;
    initEl.classList.add(
      'cmp-editorial-carousel',
      `cmp-editorial-carousel-${containerID}`,
      'swiper',
      'excursion-carousel',
      `${actionColor}`,
      '-actions-right',
      '-hide-indicator-mobile',
      '-progressbar',
      '-default',
      '-circular-actions',
      '-medium-actions'
    );

    const swiperActionsElement = createSwiperActions(
      containerID,
      carouselNextIconLabel,
      carouselPrevIconLabel
    );
    initEl.appendChild(swiperActionsElement);
    initEl.setAttribute('data-cmp-per-view', `auto`);
    initEl.setAttribute('data-cmp-is-progressbar', 'true');
    initEl.setAttribute('data-placeholder-text', 'false');
    initEl.setAttribute('aria-roledescription', 'carousel');
    initEl.setAttribute('data-cmp-is', 'carousel');
    initEl.setAttribute('aria-live', 'polite');
    initEl.setAttribute('role', 'group');
    initEl.setAttribute('data-cmp-delay', '5000');
    initEl.setAttribute('data-cmp-initial-slide', `carousel-${containerID}-item-0`);
    initNewCarousel();
  }
};

const handleHitsNode = (node: HTMLElement, element: Element, observer: MutationObserver) => {
  extractModals(element);

  element.querySelectorAll('.carousel-item-description')?.forEach((description) => {
    parseHTML(description as HTMLElement, description.textContent ?? '');
  });

  const btn = node.querySelector('.cmp-button__secondary');
  if (btn) {
    modalOnDocumentReady();
  }

  if (element.classList.contains('carousel-view')) {
    initiateCarouselOnAis(element);
  }

  observer.disconnect();
};

const setupMutationObserver = (element: Element): MutationObserver => {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const addedNodes = Array.from(mutation.addedNodes) as HTMLElement[];
      for (const node of addedNodes) {
        if (!node.tagName) continue;

        const isHits = node.classList.contains('ais-Hits');
        if (isHits) {
          handleHitsNode(node, element, observer);
        }
      }
    }
  });

  observer.observe(element, {
    childList: true,
    subtree: true,
  });

  return observer;
};

export const aisCarousel = () => {
  removeAlgoliaH2();

  const ais = document.querySelectorAll('.excursions-ais') ?? [];
  ais.forEach((element) => {
    extractModals(element);

    element.querySelectorAll('.carousel-item-description')?.forEach((description) => {
      parseHTML(description as HTMLElement, description.textContent ?? '');
    });

    checkAndInsertFallbacks(element).catch(console.error);

    if (element.querySelector('.cmp-button__secondary')) {
      modalOnDocumentReady();
    }

    if (element.classList.contains('carousel-view')) {
      initiateCarouselOnAis(element);
    }

    setupMutationObserver(element);
  });
};

//remove h2 tag from the algolia plug-in
const removeTitlesFromPanel = (panel: Element): void => {
  panel.querySelectorAll('.cmp-title__text').forEach((el) => el.remove());
};

const removeInitialTitles = () => {
  document.querySelectorAll('algolia-tab-panel .cmp-title__text').forEach((el) => el.remove());
};

function waitForFallbackUrl(timeout = 5000, interval = 100): Promise<string> {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const check = () => {
      const url = getActPlaceholderImagePath();
      if (url) {
        resolve(url);
      } else if (Date.now() - start > timeout) {
        reject(new Error('Timeout waiting for fallback url'));
      } else {
        setTimeout(check, interval);
      }
    };

    check();
  });
}

async function checkAndInsertFallbacks(root: Element) {
  try {
    const fallbackImageUrl = await waitForFallbackUrl();

    const imageContainers = root.querySelectorAll('.cmp-image');

    imageContainers.forEach((container) => {
      const img = container.querySelector('img');

      if (!img || !(img instanceof HTMLImageElement)) return;

      const handleImageError = () => {
        if (img.src === fallbackImageUrl) return;

        const oldSrc = img.src;

        // 1. Replace all images in the document with the same absolute src
        const allImages = document.querySelectorAll('img');
        allImages.forEach((el) => {
          if (el instanceof HTMLImageElement && el.src === oldSrc) {
            el.onerror = null;
            el.src = fallbackImageUrl;
          }
        });

        // 2. Traverse up to find the button with href="#some-id"
        const parentCarouselItem = img.closest('.carousel-item');
        if (!parentCarouselItem) return;

        const infoButton =
          parentCarouselItem.querySelector<HTMLAnchorElement>('a.cmp-button[href^="#"]');
        if (!infoButton) return;

        const href = infoButton.getAttribute('href');
        if (!href?.startsWith('#')) return;

        const targetId = href.substring(1); // Remove "#"
        const targetSection = document.getElementById(targetId);
        if (!targetSection) return;

        // 3. Replace the image inside the target section
        const targetImage = targetSection.querySelector('img');
        if (targetImage instanceof HTMLImageElement && targetImage.src === oldSrc) {
          targetImage.onerror = null;
          targetImage.src = fallbackImageUrl;
        }
      };

      // Attach error handler
      img.onerror = handleImageError;

      // Optional: Also listen to the 'error' event
      if (!img.complete) {
        img.addEventListener('error', handleImageError);
      }

      // If image is already loaded but broken
      if (img.complete && img.naturalWidth === 0) {
        handleImageError();
      }
    });
  } catch (e) {
    console.error('Failed to get fallback image URL:', e);
  }
}

const handleAddedNode = (node: Node): void => {
  if (!(node instanceof Element)) return;

  if (node.matches('algolia-tab-panel')) {
    removeTitlesFromPanel(node);
  } else {
    node
      .querySelectorAll('algolia-tab-panel')
      .forEach((panel: Element) => removeTitlesFromPanel(panel));
  }
};

const observeDomChanges = () => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach(handleAddedNode);
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

const removeAlgoliaH2 = () => {
  removeInitialTitles();
  observeDomChanges();
};

export const onDocumentReady = () => {
  aisCarousel();
};

if (document.readyState !== 'loading') {
  onDocumentReady();
} else {
  document.addEventListener('DOMContentLoaded', aisCarousel);
}
